/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useLoaderData, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Head } from 'vite-react-ssg';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';

// Utils
import cn from '@/utils/cn';
import { getLocale } from '@/utils/localeSlug';
import { getContactSlug } from '@/utils/contactSlug';
import { buildSEOUrl } from '@/utils/seo';

// Components
import Heading from '@/components/atoms/Heading/Heading';
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import BlocHero from '@/components/blocks/BlocHero/BlocHero';

// Styles
import ChevronDownLightIcon from '@/assets/icons/chevron-down-dark.svg?react';
import EmailIcon from '@/assets/icons/email.svg?react';
import PhoneIcon from '@/assets/icons/phone.svg?react';
import PositionIcon from '@/assets/icons/position.svg?react';
import HoursIcon from '@/assets/icons/hours.svg?react';
import SendingIcon from '@/assets/icons/sending.svg?react';
import styles from './Contact.module.css';

export async function contactLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  const locale = getLocale(url);

  const [header, footer] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
  ]);

  return {
    header,
    footer,
  };
}

export const ContactFormSchema = z.object({
  requestReason: z.string().min(1),
  companyName: z.string().min(1),
  lastName: z.string().min(1),
  firstName: z.string().min(1),
  phone: z.string().min(1),
  email: z.string()
    .min(1)
    .trim()
    .pipe(z.email()),
  message: z.string().min(1,),
  privacyCheckbox: z.literal(true),
});

export type ContactFormType = z.infer<typeof ContactFormSchema>;

function Contact() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const locale = getLocale(pathname);
  const baseUrl = import.meta.env.VITE_DOMAIN;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitted },
  } = useForm<ContactFormType>({
    resolver: zodResolver(ContactFormSchema),
    mode: 'onChange',
  });

  const onSubmit = (data: ContactFormType) => {
    // eslint-disable-next-line no-console
    console.log(data);
  };

  // API Calls
  const data = useLoaderData() as Awaited<ReturnType<typeof contactLoader>>;

  if (!data) return null;

  const { header, footer } = data;

  const infoPhone = t('contact.infoCard.phone');

  // Renders
  const renderForm = () => (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles['form-select-block']}>
        <span className={styles['form-select-label']}>
          {t('contact.form.input.requestReason.label')}
        </span>
        <select
          {...register('requestReason')}
          className={styles['form-select']}
        >
          <option value={t('contact.form.input.requestReason.options.logistics')}>
            {t('contact.form.input.requestReason.options.logistics')}
          </option>
          <option value={t('contact.form.input.requestReason.options.payment')}>
            {t('contact.form.input.requestReason.options.payment')}
          </option>
          <option value={t('contact.form.input.requestReason.options.other')}>
            {t('contact.form.input.requestReason.options.other')}
          </option>
        </select>
        <ChevronDownLightIcon className={styles['form-select-icon']} />
      </div>
      {
        errors.requestReason && (
          <div className={styles['input-error']}>{errors.requestReason.message}</div>
        )
      }
      <div className={styles['input-group']}>
        <input
          {...register('companyName')}
          placeholder=" "
          className={styles.input}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.companyName.label')}
        </label>
      </div>
      {
        errors.companyName && (
          <div className={styles['input-error']}>{errors.companyName.message}</div>
        )
      }
      <div className={styles['input-group']}>
        <input
          {...register('lastName')}
          placeholder=" "
          className={styles.input}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.lastName.label')}
        </label>
      </div>
      {errors.lastName && <div className={styles['input-error']}>{errors.lastName.message}</div>}
      <div className={styles['input-group']}>
        <input
          {...register('firstName')}
          placeholder=" "
          className={styles.input}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.firstName.label')}
        </label>
      </div>
      {errors.firstName && <div className={styles['input-error']}>{errors.firstName.message}</div>}
      <div className={styles['input-group']}>
        <input
          {...register('phone')}
          placeholder=" "
          className={styles.input}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.phone.label')}
        </label>
      </div>
      {errors.phone && <div className={styles['input-error']}>{errors.phone.message}</div>}
      <div className={styles['input-group']}>
        <input
          {...register('email')}
          placeholder=" "
          className={styles.input}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.email.label')}
        </label>
      </div>
      {errors.email && <div className={styles['input-error']}>{errors.email.message}</div>}
      <div className={styles['input-group']}>
        <textarea
          {...register('message')}
          placeholder=" "
          className={cn([
            styles.input,
            styles.textarea
          ])}
        />
        <label className={styles['input-label']}>
          {t('contact.form.input.message.label')}
        </label>
      </div>
      {errors.message && <div className={styles['input-error']}>{errors.message.message}</div>}
      <p className={styles['privacy-notice']}>
        {t('contact.form.privacyNotice')}
      </p>
      <div className={styles['checkbox-group']}>
        <input
          type="checkbox"
          {...register('privacyCheckbox')}
          id="privacyAccepted"
          className={styles.checkbox}
        />
        <label
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('contact.form.input.privacyCheckbox.label', { url: '/privacy-policy' })
          }}
        />
      </div>
      {
        errors.privacyCheckbox && (
          <div className={styles['input-error']}>
            {errors.privacyCheckbox.message}
          </div>
        )
      }
      <button
        type='submit'
        className={styles.button}
        disabled={!isValid}
      >
        {t('contact.form.send-request')}
      </button>
      <p className={styles['data-protection-notice']}>
        {t('contact.form.dataProtectionNotice')}
      </p>
    </form>
  );

  const renderSubmitted = () => (
    <div className={styles.submitted}>
      <div className={styles['submitted-description']}>
        <SendingIcon />
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('contact.form.submitted-description')
          }}
        />
      </div>

      <button
        type='button'
        className={styles.button}
        onClick={() => reset()}
      >
        {t('contact.form.new-request')}
      </button>
    </div>
  );

  return (
    <main>
      <Head htmlAttributes={{ lang: locale }}>
        <title>{t('contact.title')}</title>
        <meta name="description" content={t('contact.metaDescription')} />
        <link rel='canonical' href={buildSEOUrl(baseUrl, locale, getContactSlug(locale))} />
      </Head>
      <Header
        color='dark'
        header={header as HeaderType}
      />

      <div className={styles.body}>
        <BlocHero
          title={t('contact.title')}
        />
        <div className={styles.container}>
          <div className={styles.contact}>
            <div className={styles['form-body']}>
              <div className={styles['form-content']}>
                <Heading
                  title={isSubmitted ? t('contact.form.submitted') : t('contact.form.title')}
                  size='h2'
                  Tag='h2'
                  color='dark'
                />
                {
                  !isSubmitted ? (
                    renderForm()
                  ) : (
                    renderSubmitted()
                  )
                }
              </div>
              <div className={styles['floated-card']}>
                <div className={styles['floated-card-content']}>
                  <Heading
                    title={t('contact.infoCard.title')}
                    size='h4'
                    Tag='h2'
                    align='center'
                    color='light'
                  />
                  <div className={styles['floated-card-item']}>
                    <EmailIcon />
                    {t('contact.infoCard.email')}
                  </div>
                  {infoPhone.trim() && (
                    <div className={styles['floated-card-item']}>
                      <PhoneIcon />
                      {infoPhone}
                    </div>
                  )}
                  <div className={styles['floated-card-item']}>
                    <PositionIcon />
                    <span>
                      {
                        t('contact.infoCard.position', { returnObjects: true }).map((item: string) => (
                          <p key={item}>{item}</p>
                        ))
                      }
                    </span>
                  </div>
                  <div className={styles['floated-card-item']}>
                    <HoursIcon />
                    <span>
                      {
                        t('contact.infoCard.hours', { returnObjects: true }).map((item: string) => (
                          <p key={item}>{item}</p>
                        ))
                      }
                    </span>
                  </div>
                </div>
                <div className={styles['floated-card-figure']} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer
        footer={footer as FooterType}
      />
    </main>
  );
}

export default Contact;